import type { FormikErrors } from "formik";
import { isInteger, set } from "lodash";
import { useMemo } from "react";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import type { ZodType } from "zod";
import { z } from "zod";

import type { TranslationID } from "@citrine/lang/utils";
import type { ValidationMessages } from "@citrine/utils/validation";
import {
  getCeiling,
  getFloor,
  getValidationMessages,
} from "@citrine/utils/validation";

/** @public */
export { getValidationMessages };
export type { ValidationMessages };

/** @public */
export function useValidationMessages() {
  const intl = useIntl();
  return useMemo(() => getValidationMessages(intl), [intl]);
}

/** @public */
export const NanToUndefined = z.nan().transform(() => undefined);

/** @public */
export function schemaToValidate<Schema extends ZodType>(schema: Schema) {
  return async function validate(
    values: z.input<Schema>
  ): Promise<
    | (z.input<Schema> extends object ? FormikErrors<z.input<Schema>> : string)
    | undefined
  > {
    const { error } = await schema.safeParseAsync(values);
    if (error) {
      if (error.issues.length === 1) {
        const issue = error.issues[0];
        if (issue.path.length === 0) {
          // the schema is a scalar; return a simple error string
          return issue.message as any;
        }
      }

      const errors = {};
      error.issues.forEach((issue) => {
        /**
         * We have to use setWith+Object to coerce arrays into objects
         * This is done in case an array also has a named property
         * Formik will respect errors on objects with integer keys, but not arrays with string keys
         */
        if (issue.code === "invalid_union") {
          // filter out discriminated union errors
          issue.errors
            .filter((err) => err[0].code !== "invalid_value")
            .flat()
            .forEach((err) => {
              set(errors, [...issue.path, ...err.path], err.message);
            });
        } else {
          set(errors, issue.path, issue.message);
        }
      });
      // the schema is an object; return an error object
      return errors as any;
    }
  };
}

/** @public */
export function useSchemaToValidate<Schema extends ZodType>(schema: Schema) {
  return useMemo(() => schemaToValidate(schema), [schema]);
}

/** @public */
export function useSchemaFactoryToValidate<Schema extends ZodType>(
  factory: (validationMessages: ValidationMessages) => Schema
) {
  const validationMessages = useValidationMessages();
  return useMemo(
    () => schemaToValidate(factory(validationMessages)),
    [factory, validationMessages]
  );
}

/**
 * validation helpers
 *
 * Use inverted logic to allow NaN to pass
 */
export function createIntlValidator(intl: IntlShape) {
  const vm = getValidationMessages(intl);
  return {
    formatMessage: intl.formatMessage,

    isGreaterThan: (...bounds: number[]) => {
      const floor = getFloor(...bounds);
      return (val: number = NaN) =>
        val <= floor ? vm.greaterThan(floor) : undefined;
    },

    isGreaterThanOrEqualTo: (...bounds: number[]) => {
      const floor = getFloor(...bounds);
      return (val: number = NaN) =>
        val < floor ? vm.greaterThanOrEqualTo(floor) : undefined;
    },

    isInteger: (val: number) => (isInteger(val) ? undefined : vm.integer()),

    isLessThan: (...bounds: number[]) => {
      const ceiling = getCeiling(...bounds);
      return (val: number = NaN) =>
        val >= ceiling ? vm.lessThan(ceiling) : undefined;
    },

    isLessThanOrEqualTo: (...bounds: number[]) => {
      const ceiling = getCeiling(...bounds);
      return (val: number = NaN) =>
        val > ceiling ? vm.lessThanOrEqualTo(ceiling) : undefined;
    },

    isNotEmpty: (val: any) =>
      Array.isArray(val) && val.length ? undefined : vm.notEmpty(),

    isNumber: (val: number) => (isNaN(val) ? vm.number() : undefined),

    isPositive: (val: number = NaN) => (val < 0 ? vm.positive() : undefined),

    isRequired: (val: any) =>
      Boolean(val) || val === 0 ? undefined : vm.required(),

    isUnique: (members: any[], messageId?: TranslationID) => (val: any) =>
      members.includes(val)
        ? messageId
          ? intl.formatMessage({ id: messageId })
          : vm.unique()
        : undefined,

    isValidSelection: (val: unknown, options: unknown[]) =>
      options.includes(val) ? undefined : vm.validSelection(val),
  };
}

export type IntlValidator = ReturnType<typeof createIntlValidator>;

export function useValidation() {
  const intl = useIntl();
  return useMemo(() => createIntlValidator(intl), [intl]);
}
